import { DateFormats, ModuleType } from 'infrastructure/enums';
import {
  DateFormatter,
  isApcmModule,
  isCcmModule,
  isRpmModule,
} from 'infrastructure/functions';
import { useAddClinicalNotes, useQuestions } from 'infrastructure/hooks/ccm';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fullNamePresenter } from 'utils';
import i18next from 'i18next';
import useMonthlyInteractionAlert from 'infrastructure/hooks/utils/use-monthly-interaction-alert';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useUpdateInteractionStatus } from 'infrastructure/hooks/control-panel';
import { getCkdCcmClinicalNoteStoreKey } from 'components/organisms/ckd-clinical-note-modal/use-note';
import useUser from 'utils/useUser';

import type { IAddClinicNotes } from 'infrastructure/interfaces';
import type { Values } from 'components/molecules/admin-panel/forms/clinical-notes/validation-schema';
import type { NextAppointment } from 'infrastructure/classes/next-appointment';

interface IProps {
  patientGuid: string;
  module: ModuleType;
  onClose: () => void;
  updateNextAppointment?: (details: NextAppointment) => Promise<void>;
  getPendingAppointmentDetails?: () => NextAppointment | null;
  isAdminPanel?: boolean;
}

type AlertMessageProps = {
  name: string;
  dob: string;
  score: number;
};

const generateSaveAlertMessage = ({ name, dob, score }: AlertMessageProps) => (
  <p>
    <p>{i18next.t('confirm.confirmSaveClinicalNoteTitle')}</p>
    <p style={{ paddingBottom: 16 }}>
      {i18next.t('confirm.confirmSaveClinicalNoteSubTitle')}
    </p>
    <p style={{ paddingBottom: 16 }}>
      {i18next.t('confirm.saveClinicalNoteInfoTitle')}
    </p>
    <p>{name}</p>
    <p>
      {i18next.t('confirm.dob')} {dob}
    </p>
    <p>
      {i18next.t('confirm.riskScore')} {score}
    </p>
  </p>
);

export const useLogic = ({
  patientGuid,
  module,
  onClose,
  updateNextAppointment,
  getPendingAppointmentDetails,
  isAdminPanel,
}: IProps) => {
  const { hasClinicalRole } = useUser();
  const [defaultValue, setDefaultValue] = useState<string>();
  const [isNarrativeModalOpen, setIsNarrativeModalOpen] = useState(false);
  const [isTemplateBased, setIsTemplateBased] = useState(false);
  const [apcmTemplateType, setApcmTemplateType] =
    useState<IAddClinicNotes['templateType']>();
  const [isNextAppointmentModalOpen, setIsNextAppointmentModalOpen] =
    useState(false);
  const ccmInteractionUpdateFunction = useRef<Function | undefined>();
  const ccmNextAppointmentModalPromise = useRef<any>();

  const saveCcmInteractionUpdateFunction = (fnc: Function) => {
    ccmInteractionUpdateFunction.current = fnc;
    closeIsNextAppointmentModalOpen();
  };
  const closeIsNextAppointmentModalOpen = () => {
    if (ccmNextAppointmentModalPromise.current) {
      ccmNextAppointmentModalPromise.current();
    }
    ccmNextAppointmentModalPromise.current = undefined;
    setIsNextAppointmentModalOpen(false);
  };

  const { patientInfo } = usePatientInfo({
    patientGuid,
  });
  const { questions } = useQuestions({
    patientGuid,
    module: ModuleType.CCM,
  });
  const { updateInteractionStatus } = useUpdateInteractionStatus();

  const { addClinicalNotesAsync, addLoading: isAddLoading } =
    useAddClinicalNotes();

  const { t } = useTranslation();
  const { MonthlyInteractionAlert, showMonthlyInteractionAlert } =
    useMonthlyInteractionAlert({
      patientGuid,
    });
  const { Alert, showAlert } = useAlert();

  const riskScore = questions?.score ?? 0;

  const toggleNarrativeModal = () => setIsNarrativeModalOpen((prev) => !prev);

  const handleCancelForm = () => {
    setDefaultValue(undefined);
    setApcmTemplateType(undefined);
    onClose();
    setIsTemplateBased(false);
    const ckdCcmClinicalNoteStoreKey =
      getCkdCcmClinicalNoteStoreKey(patientGuid);
    localStorage.removeItem(ckdCcmClinicalNoteStoreKey);
  };

  const showSaveAlert = async () => {
    const fullPatientName = fullNamePresenter({
      firstName: patientInfo?.loginInfo?.firstName,
      middleName: patientInfo?.loginInfo?.middleName,
      lastName: patientInfo?.loginInfo?.lastName,
      title: patientInfo?.loginInfo?.title,
    });

    const birthDate = DateFormatter({
      date: patientInfo?.loginInfo?.birthDate,
      format: DateFormats['MM.dd.yyyy'],
    });

    const res = await showAlert({
      title: t('labels.saveNote'),
      customMessage: generateSaveAlertMessage({
        name: fullPatientName,
        score: riskScore,
        dob: birthDate,
      }),
      rejectBtnLabel: t('controls.no'),
      successBtnLabel: `${t('controls.yes')}, ${t('controls.save')}`,
    });

    return res;
  };

  const handleRpmNote = async (note: string) => {
    let interactionStatusConfirmed = false;
    if (
      !patientInfo?.interactionStatus &&
      !isTemplateBased &&
      hasClinicalRole
    ) {
      interactionStatusConfirmed = await showMonthlyInteractionAlert();
    }

    const saveConfirmRes = await showSaveAlert();

    if (!saveConfirmRes) return;

    addClinicalNotesAsync(
      {
        patientGuid,
        note,
        module,
        isTemplateBased,
        templateType: 'specialist',
        actionFrom: isAdminPanel ? 'admin-panel' : undefined,
      },
      {
        onSuccess: async () => {
          if (
            !patientInfo?.interactionStatus &&
            hasClinicalRole &&
            (isTemplateBased || interactionStatusConfirmed)
          ) {
            await updateInteractionStatus({ patientGuid, isInteracted: true });
          }
          handleCancelForm();
        },
      },
    );
  };

  const handleApcmNote = async (note: string) => {
    let interactionStatusConfirmed = false;
    if (!patientInfo?.interactionStatus && hasClinicalRole) {
      interactionStatusConfirmed = await showMonthlyInteractionAlert({
        ignoreIsShowed: true,
      });
    }

    const saveConfirmRes = await showSaveAlert();

    if (!saveConfirmRes) return;

    addClinicalNotesAsync(
      {
        patientGuid,
        note,
        module,
        isTemplateBased,
        templateType: apcmTemplateType,
      },
      {
        onSuccess: async () => {
          if (interactionStatusConfirmed) {
            await updateInteractionStatus({ patientGuid, isInteracted: true });
          }
          handleCancelForm();
        },
      },
    );
  };

  const handleCcmNote = async (note: string) => {
    let interactionStatusConfirmed = false;

    if (!patientInfo?.ccmInteractionStatus && hasClinicalRole) {
      interactionStatusConfirmed = await showMonthlyInteractionAlert({
        module: ModuleType.CCM,
      });

      if (interactionStatusConfirmed) {
        await new Promise((res) => {
          ccmNextAppointmentModalPromise.current = res;
          setTimeout(() => {
            setIsNextAppointmentModalOpen(true);
          }, 300);
        });
      }
    }

    const saveConfirmRes = await showSaveAlert();

    if (!saveConfirmRes) {
      ccmInteractionUpdateFunction.current = undefined;
      return;
    }

    await addClinicalNotesAsync(
      {
        patientGuid,
        note,
        module,
        actionFrom: isAdminPanel ? 'admin-panel' : undefined,
      },
      {
        onSuccess: async () => {
          handleCancelForm();
          if (ccmInteractionUpdateFunction.current) {
            const saveFN = ccmInteractionUpdateFunction.current();
            await saveFN.finally(() => {
              ccmInteractionUpdateFunction.current = undefined;
            });
          }
        },
      },
    );
  };

  const handleSubmit = async ({ note }: Values): Promise<boolean> => {
    if (!note?.trim()) return false;
    try {
      if (isCcmModule(module)) {
        await handleCcmNote(note);
      } else if (isRpmModule(module)) {
        await handleRpmNote(note);
      } else if (isApcmModule(module)) {
        await handleApcmNote(note);
      }
    } finally {
      const details = getPendingAppointmentDetails?.();
      if (details && updateNextAppointment) {
        await updateNextAppointment(details);
      }
    }

    return true;
  };

  return {
    defaultValue,
    MonthlyInteractionAlert,
    Alert,
    addLoading: isAddLoading,
    narrativeModalOpen: isNarrativeModalOpen,
    isNextAppointmentModalOpen,
    onSubmit: handleSubmit,
    onCancelForm: handleCancelForm,
    toggleOpenNarrativeModal: toggleNarrativeModal,
    setDefaultValue,
    setIsNextAppointmentModalOpen: closeIsNextAppointmentModalOpen,
    setIsTemplateBased,
    saveCcmInteractionUpdateFunction,
    setApcmTemplateType,
  };
};
